import _ from "lodash";
import { type CSSProperties, useMemo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { EnumColors } from "@/constants/values";
import { useEntity } from "@/hooks/useEntity";
import { useEnumColor } from "@/hooks/useEnumColor";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { BuildingIcon, UserIcon } from "lucide-react";

export function BoxForEntity({
  identifier,
  representation = "icon",
  size = "md",
}: {
  identifier?: string;
  representation?: "character" | "icon";
  size?: "xs" | "sm" | "md" | "lg";
}) {
  const { entity, isLoading } = useEntity(identifier);

  const sizeValue = useMemo(() => {
    if (size === "xs") return 16;
    if (size === "sm") return 20;
    if (size === "md") return 26;
    if (size === "lg") return 32;
  }, [size]);

  const textSizeValue = useMemo(() => {
    if (size === "xs") return "body-sm";
    if (size === "sm") return "body-md";
    if (size === "md") return "body-md";
    if (size === "lg") return "body-lg";
  }, [size]);

  // convert the identifier to a number between 0 and len(EnumColors)
  // Use the entire identifier string to generate a color index
  const colorIndex =
    Array.from(identifier || "").reduce(
      (acc, char) => acc + char.charCodeAt(0),
      0,
    ) % Object.keys(EnumColors).length;

  const { color, borderColor, backgroundColor } = useEnumColor(
    entity?.isUserEntity ? EnumColors.GREY_1 : EnumColors.GREY_2,
    entity?.isUserEntity ? "inverted" : "inverted",
  );
  // A person's own books, solid; a business's, outlined - its ground, edge and
  // words an outline button's, as the controls beside it are.
  const isOutlined = !entity?.isUserEntity;

  if (isLoading || !entity) {
    return (
      <Skeleton
        data-entity-identifier={identifier}
        variant="rectangular"
        width={sizeValue}
        height={sizeValue}
        className={cn(
          "shrink-0",
          size === "lg" ? "rounded-md" : "rounded-sm",
          size === "xs"
            ? "min-w-4"
            : size === "sm"
              ? "min-w-5"
              : size === "md"
                ? "min-w-6.5"
                : "min-w-8",
          size === "xs"
            ? "min-h-4"
            : size === "sm"
              ? "min-h-5"
              : size === "md"
                ? "min-h-6.5"
                : "min-h-8",
        )}
      />
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          data-entity-identifier={identifier}
          className={cn(
            "flex flex-row items-center justify-center [border-width:1px] [border-style:solid] select-none",
            // At a button's size, a button's corners and shadow beside them;
            // smaller, a step rounder than a hairline's.
            size === "lg" ? "rounded-md shadow-xs" : "rounded-sm",
            isOutlined
              ? "bg-background border-border text-foreground dark:bg-input/30"
              : "bg-(--entity-background) border-(--entity-border)",
            // Its square, and the room inside its edge.
            size === "xs"
              ? "p-0.5 w-4 h-4 min-w-4 min-h-4 max-w-4 max-h-4"
              : size === "sm"
                ? "p-0.5 w-5 h-5 min-w-5 min-h-5 max-w-5 max-h-5"
                : size === "md"
                  ? "p-1 w-6.5 h-6.5 min-w-6.5 min-h-6.5 max-w-6.5 max-h-6.5"
                  : "p-1 w-8 h-8 min-w-8 min-h-8 max-w-8 max-h-8",
          )}
          style={
            {
              "--entity-background": backgroundColor,
              "--entity-border": borderColor,
            } as CSSProperties
          }
        >
          {representation === "character" ? (
            <Typography
              level={textSizeValue}
              className={cn(
                "pointer-events-none select-none",
                isOutlined ? "text-foreground" : "text-(--entity-color)",
              )}
              style={{ "--entity-color": color } as CSSProperties}
            >
              {_.capitalize(entity?.name || entity?.identifier)?.[0]}
            </Typography>
          ) : (
            <Icon
              ReactIcon={entity?.isUserEntity ? UserIcon : BuildingIcon}
              style={{
                color: isOutlined ? "var(--foreground)" : color,
                pointerEvents: "none",
                userSelect: "none",
                transform: "translateX(0px)",
              }}
            />
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {entity?.name || entity?.identifier}
      </TooltipContent>
    </Tooltip>
  );
}
